import {
  Card,
  Stack,
  Typography,
  LinearProgress,
  Grid,
  Box,
  Divider,
} from '@mui/material';

import { fCurrency } from '../../../utils/formatNumber';
import { pluralize } from '../../../utils/pluralize';

interface Props {
  stats: any;
}

export default function FinanceCharts({ stats }: Props) {
  const income = stats?.totalIncome || 0;
  const expense = stats?.totalExpenditure || 0;
  const netProfit = stats?.netProfit || 0;

  const maxValue = Math.max(income, expense, 1);
  const incomeRatio = (income / maxValue) * 100;
  const expenseRatio = (expense / maxValue) * 100;
  const expenseOfIncome = income > 0 ? (expense / income) * 100 : 0;
  const isProfitable = netProfit > 0;
  const profitMargin = income > 0 ? (netProfit / income) * 100 : 0;

  const incomeExpenseDifference = income - expense;
  const reconciliation =
    stats?.totalTransactions > 0
      ? (stats.reconciledCount / stats.totalTransactions) * 100
      : 0;

  const insights: string[] = [];

  if (profitMargin >= 30) {
    insights.push(
      `Excellent profitability (${profitMargin.toFixed(1)}% profit margin).`,
    );
  } else if (profitMargin >= 10) {
    insights.push(
      `Business remains profitable (${profitMargin.toFixed(1)}% margin).`,
    );
  } else {
    insights.push('Profit margin is low. Review operating costs.');
  }

  if (expenseOfIncome > 80 && income > 0) {
    insights.push(`Expenses consume ${expenseOfIncome.toFixed(1)}% of income.`);
  } else if (income > 0) {
    insights.push(
      `Expense ratio is healthy (${expenseOfIncome.toFixed(1)}% of income).`,
    );
  }

  if (reconciliation < 50) {
    insights.push(
      `Only ${reconciliation.toFixed(1)}% of transactions are reconciled.`,
    );
  }

  if (stats?.unclassifiedCount > 20) {
    insights.push(
      `${pluralize(stats.unclassifiedCount, 'transaction')} still ${stats.unclassifiedCount === 1 ? 'requires' : 'require'} categorisation.`,
    );
  }

  if (stats?.vatRecoverable > 0) {
    insights.push(
      `Recoverable VAT available: ${fCurrency(stats.vatRecoverable)}.`,
    );
  }

  if (income === 0 && expense === 0) {
    insights.push('No financial activity available for this period.');
  }
  return (
    <Card sx={{ p: 2.5 }}>
      <Stack spacing={3}>
        <Typography variant="h6">Financial Performance Overview</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Stack spacing={1}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="body2" color="text.secondary">
                  Total Income
                </Typography>

                <Typography variant="subtitle2" fontFamily="monospace">
                  {fCurrency(income)}
                </Typography>
              </Stack>

              <LinearProgress
                variant="determinate"
                value={incomeRatio}
                sx={{
                  height: 8,
                  borderRadius: 1,
                  bgcolor: 'action.hover',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: 'success.main',
                  },
                }}
              />
            </Stack>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Stack spacing={1}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="body2" color="text.secondary">
                  Total Expenditure
                </Typography>

                <Typography variant="subtitle2" fontFamily="monospace">
                  {fCurrency(expense)}
                </Typography>
              </Stack>

              <LinearProgress
                variant="determinate"
                value={expenseRatio}
                sx={{
                  height: 8,
                  borderRadius: 1,
                  bgcolor: 'action.hover',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: 'error.main',
                  },
                }}
              />
            </Stack>
          </Grid>
        </Grid>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="body2" color="text.secondary">
            Profit Margin
          </Typography>

          <Typography
            variant="subtitle2"
            color={profitMargin >= 20 ? 'success.main' : 'warning.main'}
          >
            {profitMargin.toFixed(1)}%
          </Typography>
        </Stack>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="body2" color="text.secondary">
            Income vs Expense
          </Typography>

          <Typography
            variant="subtitle2"
            color={incomeExpenseDifference >= 0 ? 'success.main' : 'error.main'}
          >
            {fCurrency(incomeExpenseDifference)}
          </Typography>
        </Stack>
        <Divider />

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={1}
        >
          <Typography variant="subtitle1">Net Profit</Typography>

          <Typography
            variant="h6"
            fontFamily="monospace"
            sx={{
              color: isProfitable ? 'success.main' : 'error.main',
            }}
          >
            {fCurrency(netProfit)}
          </Typography>
        </Stack>

        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: 'background.neutral',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack spacing={0.75}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
              }}
            >
              Business Insights
            </Typography>

            {insights.map((insight, index) => (
              <Typography key={index} variant="body2" color="text.secondary">
                • {insight}
              </Typography>
            ))}
          </Stack>
        </Box>
      </Stack>
    </Card>
  );
}
